import {
    AfterViewInit,
    Component,
    Directive,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, timer } from 'rxjs';

import { navigateForLink } from './navigate';
import { NotificationPanelService } from './notification-panel.service';
import { TranslationKeyMap } from './notification.i18n';
import { KeyboardNavigator } from './notification.keyboard';
import {
    Notification,
    NotificationLink,
    NotificationPriority,
} from './notification.model';
import { NotificationService } from './notification.service';

export interface NotificationEvent {
    notification: Notification;
}

@Directive({
    selector: '[notificationBody]',
})
export class NotificationBodyDirective implements OnChanges {
    @Input() body: string;
    @Input() links: NotificationLink[];
    @Output() linkClick: EventEmitter<NotificationLink>;

    isCollapsed: boolean;

    constructor(private readonly _el: ElementRef) {
        this.linkClick = new EventEmitter();
    }

    ngOnChanges(changes: SimpleChanges): void {
        let body = this.body;
        if (Array.isArray(this.links)) {
            let i = 0;
            this.links.forEach((link) => {
                body = body.replace(
                    new RegExp(`\\{${i}\\}`, 'g'),
                    `<a href data-index="${i}">${link.name}</a>`,
                );
                i++;
            });
        }
        this._el.nativeElement.innerHTML = body;
        const els = Array.from<HTMLLinkElement>(
            this._el.nativeElement.querySelectorAll('a[href][data-index]'),
        );
        els.forEach((e) => {
            e.addEventListener('click', (event) => {
                event.preventDefault();
                this.linkClick.emit(this.links[e.dataset.index]);
            });
        });
    }
}

@Component({
    selector: 'cmps-notification',
    templateUrl: './notification.component.html',
    host: {
        '(keydown)': '_keyboardNavigator.handleEvent($event)',
    },
})
export class NotificationComponent implements AfterViewInit {
    @Input() notification: Notification;
    @Input() index: number;
    @Output() dismiss = new EventEmitter<NotificationEvent>();

    i18n = TranslationKeyMap;
    NotificationPriority = NotificationPriority;
    isCollapsed: boolean;
    private _dismissSub: Subscription;
    _keyboardNavigator: KeyboardNavigator;

    get isDismissed(): boolean {
        return this._dismissSub && !this._dismissSub.closed;
    }

    constructor(
        private readonly _el: ElementRef,
        private readonly _router: Router,
        private readonly _panelService: NotificationPanelService,
        private readonly _service: NotificationService,
    ) {
        this.isCollapsed = true;
    }

    ngAfterViewInit(): void {
        this._keyboardNavigator = new KeyboardNavigator(
            this._el.nativeElement.children[0],
        );
        this._keyboardNavigator.getCollapse = () => this.isCollapsed;
        this._keyboardNavigator.setCollapse = (value) => {
            this.onReadMoreClick(document.createEvent('KeyboardEvent'));
            this.isCollapsed = value;
        };
        this._keyboardNavigator.getDismiss = () => this.isDismissed;
        this._keyboardNavigator.dismiss = () =>
            this.onDismissClick(document.createEvent('KeyboardEvent'));
        this._keyboardNavigator.undo = () =>
            this.onUndoClick(document.createEvent('KeyboardEvent'));
    }

    onReadMoreClick($event: MouseEvent | KeyboardEvent): void {
        $event.preventDefault();
        this.isCollapsed = !this.isCollapsed;
        this._panelService.markAsRead(this.notification);
    }

    onLinkClick($event: NotificationLink): void {
        if (this._panelService.isOpen) {
            this._panelService.toggleOpenState();
        }

        navigateForLink(null, this._router, $event, undefined, false);
    }

    onDismissClick($event: MouseEvent | KeyboardEvent): void {
        this.onUndoClick($event);
        this._dismissSub = timer(7000).subscribe(() => {
            this.dismiss.emit({ notification: this.notification });
            this.notification.deletedOn = new Date();
            this._service
                .updateDeleteOn(this.notification)
                .subscribe(() => null);
        });
    }

    onUndoClick($event: MouseEvent | KeyboardEvent): void {
        $event.stopPropagation();
        if (this._dismissSub) {
            this._dismissSub.unsubscribe();
        }
    }
}
